import { BitkitAlertProps } from '@bitrise/bitkit-v2';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { Alert, AlertContextValue } from '@/models/Alert';

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

const AlertProvider = ({ children }: { children?: ReactNode }) => {
  const [alert, setAlert] = useState<Alert | null>(null);
  const setAlertAs = useCallback(
    (variant: BitkitAlertProps['variant'], message: ReactNode, title?: ReactNode) => {
      setAlert({ variant, message, title });
    },
    [setAlert],
  );
  const setInfoAlert = useCallback(
    (message: ReactNode, title?: ReactNode) => setAlertAs('info', message, title),
    [setAlertAs],
  );
  const setWarningAlert = useCallback(
    (message: ReactNode, title?: ReactNode) => setAlertAs('warning', message, title),
    [setAlertAs],
  );
  const setErrorAlert = useCallback(
    (message: ReactNode, title?: ReactNode) => setAlertAs('critical', message, title),
    [setAlertAs],
  );
  const setSuccessAlert = useCallback(
    (message: ReactNode, title?: ReactNode) => setAlertAs('success', message, title),
    [setAlertAs],
  );
  const setProgressAlert = useCallback(
    (message: ReactNode, title?: ReactNode) => setAlertAs('progress', message, title),
    [setAlertAs],
  );
  const removeAlert = useCallback(() => setAlert(null), [setAlert]);

  const value = useMemo(
    () => ({
      alert,
      setInfoAlert,
      setWarningAlert,
      setErrorAlert,
      setSuccessAlert,
      setProgressAlert,
      removeAlert,
    }),
    [alert, setInfoAlert, setWarningAlert, setErrorAlert, setSuccessAlert, setProgressAlert, removeAlert],
  );

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
};

const useAlert = (): AlertContextValue => {
  const context = useContext(AlertContext);

  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }

  return context;
};

export { AlertProvider };
export default useAlert;
